import React from 'react';
import { useNavigate } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useOrderForm } from '../contexts/OrderFormContext';
import DocumentUploader from '../components/DocumentUploader';
import OrderSummaryPanel from '../components/OrderSummaryPanel';
import FormInput from '../../setup/components/FormInput';

interface SecretaryOfStateFormProps {
  brand: BrandConfig;
}

const SecretaryOfStateForm: React.FC<SecretaryOfStateFormProps> = ({ brand }) => {
  const navigate = useNavigate();
  const {
    caseNumber,
    caseTitle,
    typeData,
    documents,
    updateCommonFields,
    updateTypeData,
    uploadDocument,
    removeDocument,
    submitOrder,
  } = useOrderForm();

  const data = typeData as { state?: string; jurisdiction?: string };

  return (
    <div className="flex gap-6">
      <div className="flex-1 space-y-6">
        {/* Case info */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Case Information
          </h3>
          <FormInput
            brand={brand}
            label="Case Number"
            name="caseNumber"
            value={caseNumber}
            onChange={(e) => updateCommonFields({ caseNumber: e.target.value })}
          />
          <FormInput
            brand={brand}
            label="Case Title"
            name="caseTitle"
            value={caseTitle}
            onChange={(e) => updateCommonFields({ caseTitle: e.target.value })}
          />
        </div>

        {/* Type-specific fields */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Secretary of State Filing Details
          </h3>
          <FormInput
            brand={brand}
            label="State"
            name="state"
            value={data.state ?? 'CA'}
            onChange={(e) => updateTypeData({ state: e.target.value } as Partial<typeof typeData>)}
            disabled
            helpText="Currently only California is supported"
          />
          <FormInput
            brand={brand}
            label="Jurisdiction"
            name="jurisdiction"
            value={data.jurisdiction ?? 'Sacramento'}
            onChange={(e) => updateTypeData({ jurisdiction: e.target.value } as Partial<typeof typeData>)}
            disabled
            helpText="Sacramento is the default jurisdiction"
          />
        </div>

        {/* Documents */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Documents
          </h3>
          <DocumentUploader
            brand={brand}
            documents={documents}
            onUpload={uploadDocument}
            onRemove={removeDocument}
          />
        </div>
      </div>

      <div className="w-80 flex-shrink-0 hidden lg:block">
        <OrderSummaryPanel
          brand={brand}
          onSubmit={async () => {
            try {
              await submitOrder();
              navigate('/orders');
            } catch { /* Error displayed in panel */ }
          }}
        />
      </div>
    </div>
  );
};

export default SecretaryOfStateForm;
